import TwoColumnLayout from "layout/TwoColumnLayout";
import { useLoaderData } from "react-router";
import React from "react";
import { FlightDetailSection } from "flights/FlightDetailSection";
import FlightOptionsPanel from "flights/FlightOptionsPanel";
import { FlightActivityPanel } from "./activity/FlightActivityPanel";
import { FlightItinerarySidebarApp } from "show/ItinerarySidebar/app";
import moment from "moment";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { invalidateFlightsCache } from "utilities/api/flightQuery";
import { useIsInternal } from "common/SessionContext";

export const FlightDetailApp = () => {
  const viewModel = useLoaderData<server.viewmodel.FlightDetailFrontModel>();
  const flightId = viewModel.sidePanelDto.flight.flightId;
  const queryClient = useQueryClient();
  const isInternalUser = useIsInternal();

  const sidePanelQuery = useQuery({
    queryKey: ["linkedShowsToFlight", "flight", flightId],
    queryFn: async () => {
      const result = await window.CoreApi.Flight.GetSidePanelData(flightId);
      if (result.data.hasErrors) throw new Error(result.data.errors[0].message);
      return result.data.data;
    },
    initialData: viewModel.sidePanelDto,
    initialDataUpdatedAt: Date.now(),
    staleTime: Infinity,
  });

  const { flight, passengers, linkedShows } = sidePanelQuery.data;
  const refetchSidePanelData = async () => {
    await sidePanelQuery.refetch();
    invalidateFlightsCache(queryClient);
  };

  return <TwoColumnLayout>
    <TwoColumnLayout.LeftColumn>
      <FlightDetailSection
        flight={flight}
        linkedShows={linkedShows}
        linkShowToTravel={{
          canLink: flight.permissions.canModify,
          travelType: "flight",
          travelId: flightId,
          appointmentDateTime: flight.dateTime,
          artist: flight.artist,
          linkedShowIds: linkedShows.map(show => show.showId),
          onLinked: refetchSidePanelData
        }}
        passengers={passengers}
        onUpdated={refetchSidePanelData}
      />
    </TwoColumnLayout.LeftColumn>
    <TwoColumnLayout.RightColumn>
      <FlightOptionsPanel
        optionsPanelData={viewModel.optionsPanel}
        listUrl={viewModel.listUrl}
        flightId={flightId}
      />
      {isInternalUser && <FlightItinerarySidebarApp
        flightId={flightId}
        artist={flight.artist}
        startDate={moment.utc(flight.dateTime.startDisplay)}
        endDate={moment.utc(flight.dateTime.endDisplay)}
        linkedShowIds={linkedShows.map(ls => ls.showId)}
        queryKeys="flightItinerary"
      />}
      {isInternalUser && <FlightActivityPanel
        config={viewModel.activityPanelConfig}
        flightId={flightId}
      />}
    </TwoColumnLayout.RightColumn>
  </TwoColumnLayout>
}
